/**
 * UserMenu Component
 * Following DDD principles - UI layer component
 *
 * Displays user information with dropdown menu
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { IconChevronDown } from '../Icons/IconChevron';
import { UserMenuProps } from './UserMenu.types';
import './UserMenu.scss';

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  className,
  profileTo,
  settingsTo,
  onProfileClick,
  onSettingsClick,
  onLogoutClick,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get display name
  const displayName = user?.username || user?.email || t('common.guest');
  const userImage = user?.imageURL || user?.avatar;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (callback?: () => void) => {
    setIsOpen(false);
    if (callback) {
      callback();
    }
  };

  return (
    <div
      ref={menuRef}
      className={classNames('user-menu', className, {
        'user-menu--open': isOpen,
      })}
    >
      {/* User Info Row */}
      <div className='user-menu__trigger' onClick={toggleMenu}>
        {/* User Email/Username */}
        <span className='user-menu__name'>{displayName}</span>

        {/* User Avatar */}
        <div className='user-menu__avatar'>
          {userImage ? (
            <img
              src={userImage}
              alt={displayName}
              className='user-menu__avatar-image'
            />
          ) : (
            <div className='user-menu__avatar-placeholder'>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Chevron Icon */}
        <div
          className={classNames('user-menu__chevron', {
            'user-menu__chevron--open': isOpen,
          })}
        >
          <IconChevronDown />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className='user-menu__dropdown'>
          <div className='user-menu__dropdown-content'>
            {/* Profile */}
            {profileTo ? (
              <Link
                to={profileTo}
                className='user-menu__item'
                onClick={() => setIsOpen(false)}
              >
                <span className='user-menu__item-icon'>👤</span>
                <span className='user-menu__item-label'>
                  {t('common.profile')}
                </span>
              </Link>
            ) : (
              onProfileClick && (
                <button
                  type='button'
                  className='user-menu__item'
                  onClick={() => handleMenuItemClick(onProfileClick)}
                >
                  <span className='user-menu__item-icon'>👤</span>
                  <span className='user-menu__item-label'>
                    {t('common.profile')}
                  </span>
                </button>
              )
            )}

            {/* Settings */}
            {settingsTo ? (
              <Link
                to={settingsTo}
                className='user-menu__item'
                onClick={() => setIsOpen(false)}
              >
                <span className='user-menu__item-icon'>⚙️</span>
                <span className='user-menu__item-label'>
                  {t('common.settings')}
                </span>
              </Link>
            ) : (
              onSettingsClick && (
                <button
                  type='button'
                  className='user-menu__item'
                  onClick={() => handleMenuItemClick(onSettingsClick)}
                >
                  <span className='user-menu__item-icon'>⚙️</span>
                  <span className='user-menu__item-label'>
                    {t('common.settings')}
                  </span>
                </button>
              )
            )}

            {/* Divider */}
            {(profileTo ||
              settingsTo ||
              onProfileClick ||
              onSettingsClick) &&
              onLogoutClick && <div className='user-menu__divider' />}

            {/* Logout */}
            {onLogoutClick && (
              <button
                type='button'
                className='user-menu__item user-menu__item--danger'
                onClick={() => handleMenuItemClick(onLogoutClick)}
              >
                <span className='user-menu__item-icon'>🚪</span>
                <span className='user-menu__item-label'>
                  {t('common.logout')}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
